import angular, { ILocationService, ITimeoutService } from 'angular';
import RunnerFlowService from '../runner.flow/runner.flow.service';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { RunnerFlowsFormatterService } from '@Client/runner.services/flows.formatter';
import DocumentType from '@Shared.Angular/@types/core/contracts/constants/documentType';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IntegrationState from '@Shared.Angular/@types/core/contracts/queryModel/webhook/integrationState';

/**
 * @ngdoc directive
 * @name flowFooter
 * @module flowingly.runner.flow
 * @description  This comppnent is used to display the area which contains any buttons related to the flow (Done / Withdraw).
 * @usage
 * ```
     <flow-footer ng-click="$ctrl.onClickMethod({ id: $ctrl.flow.id })" flow="$ctrl.flow" is-mobile="$ctrl.isMobile"></flow-footer>
 * ``` 
 * ### Notes
 * See Also: https://bizflo.atlassian.net/wiki/display/TECH/Angular+Flow+Components
 * ### Properties
 * #### Inputs
 * * flow: the flow data to display (JSON)
 * * isMobile: show mobile view if set
 * * canStartFlow: true if the user is able to start this flow (some views are readonly)
 */

angular.module('flowingly.runner.flow').component('flowFooter', {
  bindings: {
    flow: '<',
    step: '<',
    isMobile: '<', //show mobile view if set
    canStartFlow: '<',
    onCommentsClick: '&', //show comments tab on click
    onSaveProgress: '&',
    disableFooterButtons: '<'
  },
  controller: [
    '$location',
    '$timeout',
    'pubsubService',
    'notificationService',
    'sessionService',
    'runnerFlowService',
    'APP_CONFIG',
    'flowApiService',
    'runnerFlowsFormatter',
    'flowinglyConstants',
    'permissionsService',
    'appInsightsService',
    'busyService',
    'documentApiService',
    function (
      $location: ILocationService,
      $timeout: ITimeoutService,
      pubsubService: SharedAngular.PubSubService,
      notificationService: SharedAngular.NotificationService,
      sessionService: SharedAngular.SessionService,
      runnerFlowService: RunnerFlowService,
      APP_CONFIG: SharedAngular.APP_CONFIG,
      flowApiService: FlowApiService,
      runnerFlowsFormatter: RunnerFlowsFormatterService,
      flowinglyConstants: SharedAngular.FlowinglyConstants,
      permissionsService: SharedAngular.PermissionsService,
      appInsightsService: SharedAngular.AppInsightsService,
      busyService: SharedAngular.BusyService,
      documentApiService: SharedAngular.DocumentApiService
    ) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const $ctrl = this;
      $ctrl.runnerFlowService = runnerFlowService;
      $ctrl.busyService = busyService;
      $ctrl.doneFlow = doneFlow;
      $ctrl.showComments = showComments;
      $ctrl.withdrawFlow = withdrawFlow;
      $ctrl.canWithdrawFlow = canWithdrawFlow;
      $ctrl.canReassignStep = canReassignStep;
      $ctrl.reassignStep = reassignStep;
      $ctrl.nudgeFlowWaitingOnActor = nudgeFlowWaitingOnActor;
      $ctrl.saveProgress = saveProgress;
      $ctrl.showWaitingHistory = false;
      $ctrl.showHistoryFooter = false;
      $ctrl.exportToPdf = exportToPdf;
      $ctrl.getBackendPdf = getBackendPdf;
      $ctrl.useBackendPrinting = APP_CONFIG.enableBackendDocumentPrinting;
      $ctrl.showStepTaskButton = showStepTaskButton;
      $ctrl.addStepTask = addStepTask;
      $ctrl.isProcessingIntegration = isProcessingIntegration;
      $ctrl.canOverrideProcessingIntegration = canOverrideProcessingIntegration;
      $ctrl.overrideProcessingIntegration = overrideProcessingIntegration;
      $ctrl.timer = null; // timer for footerSubmitButton
      $ctrl.isDeleteFlowModalOpen = false;
      $ctrl.toggleDeleteFlowModal = canDeleteFlow()
        ? toggleDeleteFlowModal
        : null;
      $ctrl.deleteFlow = deleteFlow;
      $ctrl.canDeleteFlow = canDeleteFlow;

      // Reassign dialog state - local to controller following DeleteModal pattern
      $ctrl.showReassignDialog = false;
      $ctrl.reassignDialogData = null;
      $ctrl.closeReassignDialog = closeReassignDialog;

      $ctrl.$onChanges = function (changes) {
        //will be called on component creation and any time one-way inputs changed by the parent
        //we need to get the last step so we can check to see if it is waiting on this user
        if (changes && changes.flow && changes.flow.currentValue) {
          $ctrl.showWaitingHistory =
            !$ctrl.step.isCompleted &&
            !$ctrl.step.isWaitingForYou &&
            !$ctrl.flow.isFinalised;
          $ctrl.showHistoryFooter =
            $ctrl.showWaitingHistory || $ctrl.flow.isFinalised;
        }

        // $timeout to make sure button finished initialization, thus got id
        $ctrl.timer = $timeout(() => {
          const footerSubmitButton = angular.element(
            `#formSubmit_button_${$ctrl.step.flowId}`
          );
          footerSubmitButton.off('click').click($ctrl.doneFlow); // have to bind in Jquery way, if use ng-click, it will refresh the page instead
        });
      };

      $ctrl.$onDestroy = () => {
        const footerSubmitButton = angular.element(
          `#formSubmit_button_${$ctrl.step.flowId}`
        );
        if (footerSubmitButton && footerSubmitButton.length > 0) {
          footerSubmitButton.off('click');
        }
        // remove timer
        $timeout.cancel($ctrl.timer);
      };

      this.hasStarted = () => runnerFlowService.hasStarted;
      this.isAwaitingYourInput = () =>
        !this.step.isCompleted && this.step.isWaitingForYou;

      /// PUBLIC /////////////////////////////////////////////////
      function showStepTaskButton() {
        if (isProcessingIntegration()) {
          // Cannot add tasks to a step with processing integration.
          return false;
        }

        // Only the step assignee can see the button or if you're in the team assigned to the step.
        return (
          this.isAwaitingYourInput() &&
          isStepTaskSettingEnabled() &&
          this.step.stepTaskEnabled != null &&
          this.step.stepTaskEnabled === true
        );
      }

      function isStepTaskSettingEnabled() {
        const isStepTasksEnabled =
          APP_CONFIG.enableStepTasks != null && APP_CONFIG.enableStepTasks;
        return isStepTasksEnabled;
      }

      function addStepTask() {
        return runnerFlowService.addStepTask({
          stepTaskCreatedByUserId: sessionService.getUser().id,
          stepId: $ctrl.step.id,
          flowId: $ctrl.flow.flowId
        });
      }

      function showComments() {
        $ctrl.onCommentsClick();
      }

      function withdrawFlow() {
        runnerFlowService.cancelEntity({
          stepId: $ctrl.step.id,
          flowId: $ctrl.flow.flowId,
          stepTaskId: undefined,
          entity: 'flow'
        });
      }

      function toggleDeleteFlowModal() {
        $ctrl.isDeleteFlowModalOpen = !$ctrl.isDeleteFlowModalOpen;
      }

      function deleteFlow() {
        runnerFlowService.deleteFlow($ctrl.flow.flowId).then(() => {
          pubsubService.publish('NAVIGATE_BACK'); // redirects to the previous page
          toggleDeleteFlowModal();
        });
      }

      function nudgeFlowWaitingOnActor() {
        runnerFlowService.nudgeUser({
          whoIsNudged: $ctrl.step.whoIsAssignedStep,
          stepName: $ctrl.step.name,
          stepId: $ctrl.step.id,
          nudgedById: sessionService.getUser().id,
          nudgedByUserName: sessionService.getUser().fullName,
          flowSubject: $ctrl.flow.subject,
          flowIdentifier: $ctrl.flow.flowIdentifier
        });
      }

      function saveProgress() {
        appInsightsService.startEventTimer('stepFormSaved');
        if (isProcessingIntegration()) {
          // The step cannot be save/submitted while there is processing integration for the step.
          return;
        }

        $('.changed-input').removeClass('changed-input');
        $ctrl.onSaveProgress().then(() => {
          notificationService.showSuccessToast('Changes successfully saved');

          appInsightsService.trackMetricIfTimerExist(
            'stepFormSaved',
            getStepFormPropsForAppInsights()
          );
        });
      }

      /// PRIVATE /////////////////////////////////////////////////
      function doneFlow(e) {
        if (isProcessingIntegration()) {
          // The step cannot be save/submitted while there is processing integration for the step.
          return;
        }

        //remove step querystring value from url so that user don't see completed by user message after done button click.
        $location.search('step', '');
        const formSubmitButton = angular.element(
          `#formSubmit_${$ctrl.step.flowId}`
        );

        if (formSubmitButton && formSubmitButton.length > 0) {
          e.preventDefault();
          e.stopPropagation();

          formSubmitButton.click(); // trigger the submit button inside the form to correctly function
        }
      }

      function canDeleteFlow() {
        return (
          APP_CONFIG.enableFlowDelete &&
          permissionsService.currentUserHasPermission(
            flowinglyConstants.permissions.FLOW_DELETE
          )
        );
      }

      function canWithdrawFlow() {
        return (
          !$ctrl.step.isCompleted &&
          ($ctrl.flow.isStartedByYou ||
            $ctrl.flow.isFlowModelOwner ||
            permissionsService.currentUserHasPermission(
              flowinglyConstants.permissions.FLOW_CANCEL
            ))
        );
      }

      function canReassignStep() {
        if (isProcessingIntegration()) {
          // Cannot reassign when integration is processing.
          return false;
        }

        return (
          !$ctrl.step.isCompleted &&
          ($ctrl.flow.isFlowModelOwner ||
            permissionsService.currentUserHasPermission(
              flowinglyConstants.permissions.FLOW_STEP_REASSIGN
            ))
        );
      }

      function isProcessingIntegration() {
        return (
          $ctrl.step != null &&
          $ctrl.step.integrationState != null &&
          $ctrl.step.integrationState.state != null &&
          $ctrl.step.integrationState.state === IntegrationState.Processing
        );
      }

      function canOverrideProcessingIntegration() {
        // Skip button visibility.
        return permissionsService.currentUserHasPermission(
          flowinglyConstants.permissions.FLOW_WEBHOOK_SKIP
        );
      }

      function overrideProcessingIntegration() {
        const user = sessionService.getUser();

        if (user != null) {
          return runnerFlowService.overrideProcessingIntegration({
            currentStep: $ctrl.step,
            stepId: $ctrl.step.id,
            flowId: $ctrl.flow.flowId,
            userId: user.id
          });
        }
      }

      function getBackendPdf() {
        documentApiService.downloadFlowPdf(
          $ctrl.flow.flowId,
          DocumentType.Flow
        );
      }

      function exportToPdf() {
        if (isStepTaskSettingEnabled()) {
          // We need to reload the flow so that any step task details, activities, etc are correct in the report.
          return flowApiService
            .getFlowById($ctrl.flow.flowId, false)
            .then((flowForUser) => {
              const formattedFlow =
                runnerFlowsFormatter.formatFlow(flowForUser);
              const pdfName = getPdfName(formattedFlow);
              return runnerFlowService.exportToPdf(
                pdfName,
                formattedFlow as any
              );
            })
            .catch(() => {
              // Fall back to the flow in the browser if we fail to reload it from the API.
              const pdfName = getPdfName($ctrl.flow);
              return runnerFlowService.exportToPdf(pdfName, $ctrl.flow);
            });
        } else {
          // Step tasks are disabled so no need to reload the flow.
          const pdfName = getPdfName($ctrl.flow);
          return runnerFlowService.exportToPdf(pdfName, $ctrl.flow);
        }
      }

      function getPdfName(flow) {
        const { flowIdentifier, subject } = flow;
        const safeSubject = subject.match(/[a-zA-Z]*/g).join('_');
        const pdfName = `${flowIdentifier}_${safeSubject}`;
        return pdfName;
      }

      function reassignStep() {
        $ctrl.reassignDialogData = {
          reassignType: flowinglyConstants.reassignType.Step,
          currentStep: $ctrl.step,
          currentTask: null,
          flowId: $ctrl.flow.flowId,
          stepId: $ctrl.step.id,
          flowIdentifier: $ctrl.flow.flowIdentifier
        };
        $ctrl.showReassignDialog = true;
      }

      function closeReassignDialog() {
        $ctrl.showReassignDialog = false;
        $ctrl.reassignDialogData = null;
      }

      function getStepFormPropsForAppInsights() {
        return {
          flowIdentifier: $ctrl.flow.flowIdentifier,
          stepName: $ctrl.step.name,
          stepTypeName:
            flowinglyConstants.stepTypeName[
              ActivityTaskType[$ctrl.step.stepType]
            ]
        };
      }
    }
  ],
  templateUrl:
    'Client/runner.flow/runner.flow.footer/runner.flow.footer.tmpl.html'
});
